/**
 * ════════════════════════════════════════════════════════════════════════
 *  THE BRAND — one source of truth for every name, prefix and identifier
 * ════════════════════════════════════════════════════════════════════════
 *
 *  Renaming this product twice taught the lesson: brand tokens spread into
 *  regexes, storage keys, PDF metadata, filenames and injected script ids, and
 *  every one of them is a place a rename can be missed silently.
 *
 *  Nothing outside this file may hardcode the product name, the quote prefix,
 *  the storage namespace or the domain. `npm run check:brand` fails the build
 *  on any retired token; the `brand` test suite proves the derived values below
 *  still agree with each other.
 *
 *  The one unavoidable duplicate is `public/theme-init.js`, which must run
 *  before any module loads and therefore cannot import this file. The brand
 *  suite asserts the two agree.
 */

const NAME = 'QuotyLab';
const DOMAIN = 'quotylab.com';

/** Lower-case, filesystem- and key-safe form of the name. */
const SLUG = NAME.toLowerCase();

export const BRAND = {
  /** Product name as displayed to people. */
  name: NAME,
  tagline: 'Generate professional quotes in under 30 seconds.',

  domain: DOMAIN,
  url: `https://${DOMAIN}`,

  /** Prefix for generated quote numbers: `QT-2026-000001`. */
  quotePrefix: 'QT',

  /** Leading segment of every exported filename. */
  filePrefix: NAME,

  /** Namespace for every persisted key: `quotylab:v1:settings`. */
  storageNamespace: SLUG,

  /** Probe written and removed to detect a usable storage backend. */
  storageProbeKey: `__${SLUG}_probe__`,

  /** Id of the analytics script tag, so it can be removed on withdrawal. */
  analyticsScriptId: `${SLUG}-plausible`,

  /** Tag on unhandled-error console output. */
  logTag: `[${NAME}]`,

  /** Credit line on free-plan PDFs. */
  pdfCredit: `Generated using ${NAME}`,
} as const;
